import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatINR, fmtDate } from "@/lib/roles";
import { useAuth } from "@/lib/auth";
import { Search, Loader2, FileCheck, Plus } from "lucide-react";

const STATUS_COLOR = {
  submitted: "outline",
  under_review: "outline",
  inspection_scheduled: "outline",
  inspection_completed: "outline",
  approved: "default",
  renewed: "default",
  rejected: "destructive",
  expired: "destructive",
};

export default function LicenseList() {
  const { user } = useAuth();
  const isCitizen = user?.role === "citizen";
  const base = isCitizen ? "/portal/licenses" : "/licenses";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const limit = 15;

  const fetchList = () => {
    setLoading(true);
    const params = { page, limit };
    if (q) params.q = q;
    if (status && status !== "all") params.status = status;
    api.get("/licenses", { params }).then((r) => {
      setItems(r.data.items || []); setTotal(r.data.total || 0);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, [page, status]);

  return (
    <div className="space-y-6" data-testid="license-list-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Module</div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold flex items-center gap-2">
            <FileCheck className="w-6 h-6 text-[#0D7650]" /> Trade Licenses
          </h1>
          <p className="text-sm text-slate-500 mt-1">Applications · inspections · approval · renewal.</p>
        </div>
        <Link to={`${base}/new`}><Button className="bg-[#0F2942] hover:bg-[#1E3A5F] text-white" data-testid="btn-license-new"><Plus className="w-4 h-4 mr-1" /> New Application</Button></Link>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <form onSubmit={(e) => { e.preventDefault(); setPage(1); fetchList(); }} className="relative flex-1 min-w-[240px] max-w-lg">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by code, business or owner" className="pl-9" data-testid="license-search" />
          </form>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-48" data-testid="license-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="under_review">Under review</SelectItem>
              <SelectItem value="inspection_scheduled">Inspection scheduled</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="renewed">Renewed</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="license-table">
                <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left py-3 px-4">License Code</th>
                    <th className="text-left py-3 px-4">Business / Owner</th>
                    <th className="text-left py-3 px-4 hidden md:table-cell">Type</th>
                    <th className="text-right py-3 px-4">Fee</th>
                    <th className="text-left py-3 px-4">Expiry</th>
                    <th className="text-left py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((a) => (
                    <tr key={a.id} className="border-t border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="py-3 px-4"><Link to={`${base}/${a.id}`} className="font-mono text-xs text-[#0F2942] dark:text-blue-300 underline" data-testid={`license-link-${a.license_code}`}>{a.license_code}</Link></td>
                      <td className="py-3 px-4">
                        <div className="font-medium">{a.business_name}</div>
                        <div className="text-xs text-slate-500 truncate max-w-xs">{a.owner_name} · {a.address}</div>
                      </td>
                      <td className="py-3 px-4 hidden md:table-cell text-xs capitalize">{(a.business_type || "").replace(/_/g, " ")}</td>
                      <td className="py-3 px-4 text-right font-mono">{formatINR(a.fee_amount)}</td>
                      <td className="py-3 px-4 text-xs">{fmtDate(a.expiry_date) || "—"}</td>
                      <td className="py-3 px-4">
                        <Badge className="capitalize" variant={STATUS_COLOR[a.status] || "outline"}>{(a.status || "").replace(/_/g, " ")}</Badge>
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-slate-500">No trade licenses found.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm">
        <div className="text-slate-500">Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
          <Button size="sm" variant="outline" disabled={page * limit >= total} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
}
